import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import IconButtonV2 from '../IconButtonV2';
import LoaderSkeleton from '../LoaderSkeleton';
import Spinner from '../Spinner';
import TextArea from '../TextArea';
import { ArrowRightIcon } from '../../icons';

import styles from './_index.module.scss';

const SEGMENT_DELAY_MS = 800;
type SegmentTimeoutId = ReturnType<typeof setTimeout>;

const splitAssistantMessageSegments = (value: string) =>
  value
    .split(/\n{2,}/)
    .map((segment) => segment.trim())
    .filter(Boolean);

export type ChatMessage = {
  [key: string]: unknown;
  content?: ReactNode;
  id: string;
  isStreaming?: boolean;
  role: ChatRole;
  type?: string;
  variant?: ChatMessageVariant;
};

export type ChatMessageVariant = 'default' | 'status';

export type ChatRole = 'assistant' | 'user';

export type ChatWindowMarkdownMode = 'all' | 'assistant' | 'none';

export interface ChatWindowProps {
  autoScroll?: boolean;
  className?: string;
  footerHint?: ReactNode;
  headerAction?: ReactNode;
  headerTitle?: string;
  inputDisabled?: boolean;
  inputValue?: string;
  isAgentStreaming?: boolean;
  messages?: ChatMessage[];
  onChangeInput?: (value: string) => void;
  onSubmit?: (value: string) => void;
  renderMarkdown?: ChatWindowMarkdownMode;
  renderMessage?: (message: ChatMessage) => ReactNode | undefined;
  submitDisabled?: boolean;
}

const ChatWindow = ({
  autoScroll = true,
  className,
  footerHint,
  headerAction,
  headerTitle,
  inputDisabled = false,
  inputValue = '',
  isAgentStreaming = false,
  messages = [],
  onChangeInput,
  onSubmit,
  renderMarkdown = 'assistant',
  renderMessage,
  submitDisabled = false,
}: ChatWindowProps) => {
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [pendingInput, setPendingInput] = useState<null | string>(null);
  const [visibleSegmentCounts, setVisibleSegmentCounts] = useState<Record<string, number>>({});

  const generatedMessageCounter = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const forceScrollRef = useRef(false);
  const segmentCacheRef = useRef<Record<string, string[]>>({});
  const segmentTimeoutsRef = useRef<Record<string, SegmentTimeoutId[]>>({});
  const visibleSegmentCountsRef = useRef<Record<string, number>>({});

  const combinedMessages = useMemo(
    () => [...messages, ...localMessages],
    [messages, localMessages]
  );
  const customMessageContentById = useMemo(() => {
    const contentById = new Map<string, ReactNode>();
    if (!renderMessage) return contentById;

    combinedMessages.forEach((message) => {
      const customContent = renderMessage(message);
      if (customContent !== undefined) {
        contentById.set(message.id, customContent);
      }
    });

    return contentById;
  }, [combinedMessages, renderMessage]);

  const previousMessagesRef = useRef<string[]>([]);

  useEffect(() => {
    visibleSegmentCountsRef.current = visibleSegmentCounts;
  }, [visibleSegmentCounts]);

  useEffect(() => {
    const nextMessageIds = messages.map((message) => message.id);
    const previousMessageIds = previousMessagesRef.current;
    const hasNewRemoteMessage =
      nextMessageIds.length !== previousMessageIds.length ||
      nextMessageIds.some((id, index) => id !== previousMessageIds[index]);

    if (hasNewRemoteMessage && nextMessageIds.length) {
      forceScrollRef.current = true;
    }

    previousMessagesRef.current = nextMessageIds;
  }, [messages]);

  const assistantMessageCount = useMemo(
    () => messages.filter((message) => message.role === 'assistant').length,
    [messages]
  );

  const assistantCountSnapshotRef = useRef<number>(assistantMessageCount);
  const isAgentStreamingSnapshotRef = useRef<boolean>(isAgentStreaming);

  const hasStreamingAssistantMessage = combinedMessages.some(
    (message) => message.role === 'assistant' && message.isStreaming
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const assistantMessages = combinedMessages.filter(
      (message) =>
        message.role === 'assistant' &&
        !customMessageContentById.has(message.id) &&
        typeof message.content === 'string' &&
        message.content.trim()
    );

    const activeIds = new Set<string>();
    const nextVisibleCounts: Record<string, number> = {};

    assistantMessages.forEach((message) => {
      const contentString = message.content as string;
      const segments = splitAssistantMessageSegments(contentString);
      const totalSegments = segments.length;
      const isStreamingMessage = Boolean(message.isStreaming);
      const existingVisibleCount = visibleSegmentCountsRef.current[message.id];

      segmentCacheRef.current[message.id] = segments;
      activeIds.add(message.id);

      const nextVisibleCount = isStreamingMessage
        ? (existingVisibleCount ?? 1)
        : Math.max(existingVisibleCount ?? 0, totalSegments);
      nextVisibleCounts[message.id] = nextVisibleCount;

      if (!isStreamingMessage) {
        const existingTimers = segmentTimeoutsRef.current[message.id];
        if (existingTimers) {
          existingTimers.forEach((timeoutId) => clearTimeout(timeoutId));
          delete segmentTimeoutsRef.current[message.id];
        }
        return;
      }

      const currentVisibleCount = nextVisibleCounts[message.id] ?? 1;
      const existingTimerCount = segmentTimeoutsRef.current[message.id]?.length ?? 0;
      const targetTimerCount = Math.max(totalSegments - currentVisibleCount, 0);
      const timersToAdd = targetTimerCount - existingTimerCount;

      if (timersToAdd > 0) {
        const newTimers = Array.from({ length: timersToAdd }, (_, index) =>
          setTimeout(
            () => {
              setVisibleSegmentCounts((previous) => {
                const currentCount = previous[message.id] ?? 1;
                if (currentCount >= totalSegments) return previous;

                return {
                  ...previous,
                  [message.id]: currentCount + 1,
                };
              });
            },
            SEGMENT_DELAY_MS * (existingTimerCount + index + 1)
          )
        );

        segmentTimeoutsRef.current[message.id] = [
          ...(segmentTimeoutsRef.current[message.id] ?? []),
          ...newTimers,
        ];
      }
    });

    Object.keys(segmentCacheRef.current).forEach((messageId) => {
      if (activeIds.has(messageId)) return;

      const timers = segmentTimeoutsRef.current[messageId];
      if (timers) {
        timers.forEach((timeoutId) => clearTimeout(timeoutId));
        delete segmentTimeoutsRef.current[messageId];
      }

      delete segmentCacheRef.current[messageId];
    });

    setVisibleSegmentCounts((previous) => {
      let hasChanges = false;
      const nextState = { ...previous };

      Object.keys(nextState).forEach((messageId) => {
        if (!activeIds.has(messageId)) {
          delete nextState[messageId];
          hasChanges = true;
        }
      });

      Object.entries(nextVisibleCounts).forEach(([messageId, count]) => {
        if (nextState[messageId] !== count) {
          nextState[messageId] = count;
          hasChanges = true;
        }
      });

      return hasChanges ? nextState : previous;
    });
  }, [combinedMessages, customMessageContentById]);

  useEffect(() => {
    return () => {
      Object.values(segmentTimeoutsRef.current).forEach((timeouts) => {
        timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      });
    };
  }, []);

  const messagesToRender = useMemo(() => {
    const shouldInsertStreamingPlaceholder = isAgentStreaming && !hasStreamingAssistantMessage;
    if (!shouldInsertStreamingPlaceholder) return combinedMessages;

    const streamingPlaceholder: ChatMessage = {
      content: '',
      id: 'assistant-streaming-placeholder',
      isStreaming: true,
      role: 'assistant',
      variant: 'status',
    };

    return [...combinedMessages, streamingPlaceholder];
  }, [combinedMessages, hasStreamingAssistantMessage, isAgentStreaming]);

  const showStreamingSkeleton = isAgentStreaming && !hasStreamingAssistantMessage;
  const shouldShowInputLoader = awaitingResponse || showStreamingSkeleton;

  const isSubmitDisabled = inputDisabled || submitDisabled || awaitingResponse;
  const hasInputText = inputValue.trim().length > 0;
  const inputDisplayValue = awaitingResponse ? (pendingInput ?? inputValue) : inputValue;
  const isTextAreaDisabled = inputDisabled || awaitingResponse;

  const shouldRenderMarkdownForRole = (role: ChatRole) => {
    if (renderMarkdown === 'none') return false;
    if (renderMarkdown === 'all') return true;
    return role === 'assistant';
  };

  useEffect(() => {
    if (!messages.length) {
      setLocalMessages([]);
    }
  }, [messages]);

  useEffect(() => {
    if (!awaitingResponse && localMessages.length) {
      setLocalMessages([]);
    }
  }, [awaitingResponse, localMessages.length]);

  useEffect(() => {
    if (!autoScroll) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const threshold = 80;
    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + container.clientHeight);
    const isNearBottom = distanceFromBottom <= threshold;
    const shouldForceScroll = forceScrollRef.current;

    if (shouldForceScroll || isNearBottom) {
      container.scrollTop = container.scrollHeight;
      forceScrollRef.current = false;
    }
  }, [autoScroll, combinedMessages, isAgentStreaming]);

  useEffect(() => {
    const prevAssistantCount = assistantCountSnapshotRef.current;
    const prevIsAgentStreaming = isAgentStreamingSnapshotRef.current;

    const assistantArrived = awaitingResponse && assistantMessageCount > prevAssistantCount;
    const agentFinished = awaitingResponse && prevIsAgentStreaming && !isAgentStreaming;

    if (assistantArrived || agentFinished) {
      setAwaitingResponse(false);
      setPendingInput(null);
    }

    assistantCountSnapshotRef.current = assistantMessageCount;
    isAgentStreamingSnapshotRef.current = isAgentStreaming;
  }, [assistantMessageCount, awaitingResponse, isAgentStreaming]);

  const handleChangeInput = (value: string) => onChangeInput?.(value);

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    const nextMessage: ChatMessage = {
      content: trimmedValue,
      id: `local-user-${Date.now()}-${generatedMessageCounter.current}`,
      role: 'user',
    };
    generatedMessageCounter.current += 1;

    assistantCountSnapshotRef.current = assistantMessageCount;
    isAgentStreamingSnapshotRef.current = isAgentStreaming;

    setPendingInput(trimmedValue);
    setAwaitingResponse(true);
    setLocalMessages((previousMessages) => [...previousMessages, nextMessage]);
    forceScrollRef.current = true;

    onSubmit?.(trimmedValue);
    onChangeInput?.('');
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    handleSubmit();
  };

  return (
    <div className={classNames(styles['chat-window'], className)}>
      {headerTitle && (
        <div className={styles['chat-header']} data-testid="chat-header">
          <h3> {headerTitle} </h3>
          {headerAction && <div className={styles['chat-header-action']}>{headerAction}</div>}
        </div>
      )}
      <div className={styles['chat-body']} ref={scrollContainerRef}>
        <div aria-live="polite" className={styles['chat-messages']}>
          <div className={styles['message-list']}>
            {messagesToRender.map((message) => {
              const hasCustomContent = customMessageContentById.has(message.id);
              const customContent = customMessageContentById.get(message.id);
              const isStatusMessage = message.variant === 'status';
              const shouldShowMessageLoader =
                !hasCustomContent &&
                message.role === 'assistant' &&
                Boolean(message.isStreaming) &&
                isAgentStreaming;
              const isStringContent = typeof message.content === 'string';
              const contentString = isStringContent ? (message.content as string) : '';
              const segments =
                isStringContent && !hasCustomContent
                  ? (segmentCacheRef.current[message.id] ??
                    splitAssistantMessageSegments(contentString))
                  : null;

              if (isStringContent && segments && !segmentCacheRef.current[message.id]) {
                segmentCacheRef.current[message.id] = segments;
              }

              const totalSegments = segments?.length ?? 0;
              const visibleSegmentCount =
                segments && totalSegments > 0
                  ? (visibleSegmentCounts[message.id] ?? totalSegments)
                  : null;
              const hasPendingSegments =
                segments && visibleSegmentCount !== null && visibleSegmentCount < totalSegments;
              const visibleContent =
                segments && visibleSegmentCount !== null
                  ? segments.slice(0, visibleSegmentCount).join('\n\n')
                  : isStringContent
                    ? contentString
                    : null;
              const contentToRender =
                typeof visibleContent === 'string' ? visibleContent : message.content;

              return (
                <div
                  className={classNames(styles['message'], styles[`is-${message.role}`], {
                    [styles['message-status']]: isStatusMessage,
                  })}
                  key={message.id}
                >
                  <div
                    className={classNames(styles['message-bubble'], {
                      [styles['has-custom-content']]: hasCustomContent,
                    })}
                    data-custom-content={hasCustomContent || undefined}
                    data-testid={`chat-message-bubble-${message.id}`}
                  >
                    <div className={styles['message-content']}>
                      {hasCustomContent ? (
                        customContent
                      ) : typeof contentToRender === 'string' ? (
                        shouldRenderMarkdownForRole(message.role) ? (
                          <ReactMarkdown>{contentToRender}</ReactMarkdown>
                        ) : (
                          contentToRender
                        )
                      ) : (
                        message.content
                      )}

                      {hasPendingSegments && (
                        <div
                          className={styles['message-content-loader']}
                          data-testid="chat-message-content-loader"
                        >
                          <LoaderSkeleton
                            className={styles['message-loader']}
                            height={50}
                            width={240}
                          >
                            <rect height="8" rx="4" ry="4" width="200" x="0" y="6" />
                            <rect height="8" rx="4" ry="4" width="160" x="0" y="24" />
                            <rect height="8" rx="4" ry="4" width="120" x="0" y="42" />
                          </LoaderSkeleton>
                        </div>
                      )}

                      {shouldShowMessageLoader && (
                        <div
                          className={styles['message-content-loader']}
                          data-testid="chat-message-content-loader"
                        >
                          <LoaderSkeleton
                            className={styles['message-loader']}
                            height={50}
                            width={240}
                          >
                            <rect height="8" rx="4" ry="4" width="200" x="0" y="6" />
                            <rect height="8" rx="4" ry="4" width="160" x="0" y="24" />
                            <rect height="8" rx="4" ry="4" width="120" x="0" y="42" />
                          </LoaderSkeleton>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles['chat-footer']}>
        {footerHint && <div className={styles['chat-footer-hint']}>{footerHint}</div>}

        <div
          className={classNames(styles['chat-input-field'], {
            [styles['chat-input-field-loading']]: shouldShowInputLoader,
          })}
        >
          <div className={styles['chat-input-textarea']}>
            <TextArea
              autoGrow
              className={styles['chat-input-control']}
              isDisabled={isTextAreaDisabled}
              onChange={handleChangeInput}
              onKeyDown={handleInputKeyDown}
              placeholder="How can I help?"
              rows={1}
              value={inputDisplayValue}
            />
          </div>

          {shouldShowInputLoader ? (
            <span
              className={classNames(
                styles['chat-input-submit'],
                styles['chat-input-submit-spinner']
              )}
              data-testid="chat-input-submit-spinner"
            >
              <Spinner className={styles['chat-input-spinner']} />
            </span>
          ) : (
            <span
              className={classNames(styles['chat-input-submit'], {
                [styles['chat-input-submit-filled']]: hasInputText,
              })}
            >
              <IconButtonV2
                aria-label="Submit prompt"
                disabled={isSubmitDisabled || !hasInputText}
                icon={<ArrowRightIcon />}
                onClick={handleSubmit}
                size="small"
                variant="subtle"
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
